/**
 * The admin console's API layer.
 *
 * Every call goes to the same API gateway the tenant app uses. What makes
 * these endpoints administrative is not the URL but WHO is calling: the
 * services check `isPlatformAdmin` on the verified token, so there is no
 * separate privileged network path to secure.
 *
 * This console manages ORGANIZATIONS. There is deliberately no platform-wide
 * user-management surface here — organization users are read inside the
 * organization they belong to (`organizationUsers` below), which is the only
 * shape the API offers.
 */
import { api } from '../lib/apiClient.js';

// --- authentication --------------------------------------------------------

export const login = (credentials) => api.post('/auth/login', credentials, { skipTenant: true });
export const logout = (refreshToken) => api.post('/auth/logout', { refreshToken }, { skipTenant: true });
export const me = () => api.get('/auth/me', { skipTenant: true });

// --- organizations ---------------------------------------------------------
//
// `/organizations/*` is the platform-administration surface, on its own
// gateway prefix rather than under /tenancy, so the authorization boundary is
// visible in the URL. Every route behind it is platform-admin only.

/** Every organization on the platform, each row carrying its owner. */
export const listOrganizations = (query, options) =>
  api.list('/organizations', query, { ...options, skipTenant: true });

/** Counts and the newest organizations, for the dashboard. */
export const platformOverview = (options) => api.get('/organizations/overview', { ...options, skipTenant: true });

/**
 * Creates an organization and its initial owner in one operation.
 *
 * The response carries `temporaryPassword` exactly once. Show it, let the
 * administrator copy it, and never store it — no later read returns it.
 */
export const createOrganization = (payload) => api.post('/organizations', payload, { skipTenant: true });

export const getOrganization = (id, options) => api.get(`/organizations/${id}`, { ...options, skipTenant: true });

/** The users inside ONE organization. */
export const organizationUsers = (id, query, options) =>
  api.list(`/organizations/${id}/users`, query, { ...options, skipTenant: true });

export const updateOrganization = (id, payload) => api.patch(`/organizations/${id}`, payload, { skipTenant: true });

export const setOrganizationStatus = (id, status) =>
  api.patch(`/organizations/${id}/status`, { status }, { skipTenant: true });

export const deleteOrganization = (id) => api.delete(`/organizations/${id}`, { skipTenant: true });

// --- an organization's branches, modules, settings and activity ------------
//
// These read through the tenant-scoped API with an explicit organization id.
// A platform administrator is allowed through it on the strength of the
// verified `isPlatformAdmin` claim; nothing else can reach another
// organization's data this way.

export const organizationBranches = (tenantId, query, options) =>
  api.list(`/tenancy/tenants/${tenantId}/branches`, query, { ...options, tenantId });

export const organizationModules = (tenantId, options) =>
  api.get(`/platform/tenants/${tenantId}/modules`, { ...options, tenantId });

export const organizationSettings = (tenantId, options) =>
  api.get(`/platform/tenants/${tenantId}/settings`, { ...options, tenantId });

export const organizationActivity = (tenantId, query, options) =>
  api.list(`/platform/tenants/${tenantId}/audit-logs`, query, { ...options, tenantId });

// --- module catalog (platform) --------------------------------------------

export const listModules = (query) => api.get('/platform/modules', { query, skipTenant: true });
export const createModule = (payload) => api.post('/platform/modules', payload, { skipTenant: true });
export const updateModule = (id, payload) => api.put(`/platform/modules/${id}`, payload, { skipTenant: true });
export const setModuleStatus = (id, status) =>
  api.patch(`/platform/modules/${id}/status`, { status }, { skipTenant: true });
export const deleteModule = (id) => api.delete(`/platform/modules/${id}`, { skipTenant: true });

// --- module requests --------------------------------------------------------
//
// Organizations ask for an optional module (Settings > Modules in the app);
// approving here switches it on for them, rejecting records the reason they
// will see.

export const listModuleRequests = (query, options) =>
  api.list('/platform/module-requests', query, { ...options, skipTenant: true });

export const pendingModuleRequestCount = (options) =>
  api.get('/platform/module-requests/pending-count', { ...options, skipTenant: true });

export const approveModuleRequest = (id, note) =>
  api.post(`/platform/module-requests/${id}/approve`, note ? { note } : {}, { skipTenant: true });

export const rejectModuleRequest = (id, reason) =>
  api.post(`/platform/module-requests/${id}/reject`, { reason }, { skipTenant: true });

/** Switches a module on for an organization directly (platform administrators only). */
export const enableOrganizationModule = (tenantId, moduleId) =>
  api.post(`/platform/tenants/${tenantId}/modules/${moduleId}/enable`, undefined, { tenantId });

export const disableOrganizationModule = (tenantId, moduleId) =>
  api.post(`/platform/tenants/${tenantId}/modules/${moduleId}/disable`, undefined, { tenantId });

// --- plans ----------------------------------------------------------------

export const listPlans = () => api.get('/platform/plans', { skipTenant: true });
export const createPlan = (payload) => api.post('/platform/plans', payload, { skipTenant: true });
export const updatePlan = (id, payload) => api.put(`/platform/plans/${id}`, payload, { skipTenant: true });
export const setPlanStatus = (id, status) =>
  api.patch(`/platform/plans/${id}/status`, { status }, { skipTenant: true });

// --- platform health -------------------------------------------------------

/**
 * The gateway's aggregate view of every service. Public on the gateway (it is
 * an uptime endpoint), and shown here so an administrator can see the platform
 * state without a terminal.
 */
export const servicesHealth = () => api.get('/_services/health', { skipTenant: true });
